import { COLORS, TYPOGRAPHY, SPACING, BORDER_RADIUS, SHADOWS, BREAKPOINTS } from "../constants/theme"

// Layout Grid
export const gridRowStyle = {
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	gap: `${SPACING.sm} ${SPACING.lg}`,
	marginBottom: 0,
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const gridItemStyle = {
	marginBottom: 0,
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Card Box
export const cardStyle = {
	background: COLORS.white,
	borderRadius: `0 0 ${BORDER_RADIUS.xl} ${BORDER_RADIUS.xl}`,
	padding: `${SPACING.xl} 28px`,
	boxShadow: SHADOWS.md,
	border: `1px solid ${COLORS.border}`,
	marginBottom: SPACING.xl,
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Info Box
export const infoBoxStyle = {
	color: COLORS.gray700,
	background: COLORS.backgroundLight,
	borderRadius: BORDER_RADIUS.md,
	padding: `10px 14px`,
	marginBottom: 10,
	whiteSpace: "pre-line",
	fontSize: TYPOGRAPHY.fontSize.sm,
	lineHeight: TYPOGRAPHY.lineHeight.relaxed,
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Section Title
export const sectionTitleStyle = {
	fontSize: TYPOGRAPHY.fontSize.md,
	fontWeight: TYPOGRAPHY.fontWeight.bold,
	marginBottom: SPACING.lg,
	marginTop: SPACING.md,
	borderBottom: `2px solid ${COLORS.primary}`,
	paddingBottom: SPACING.sm,
	color: COLORS.primary,
	letterSpacing: "0.2px",
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Filter Styles
export const filterInputStyle = {
	border: `1px solid ${COLORS.borderDark}`,
	borderRadius: BORDER_RADIUS.md,
	padding: `${SPACING.sm} ${SPACING.sm}`,
	fontSize: TYPOGRAPHY.fontSize.sm,
	height: "40px",
	background: COLORS.white,
	color: COLORS.gray700,
	outline: "none",
	transition: "border-color 0.2s",
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const filterSelectStyle = {
	border: `1px solid ${COLORS.borderDark}`,
	borderRadius: BORDER_RADIUS.md,
	padding: `${SPACING.sm} ${SPACING.sm}`,
	fontSize: TYPOGRAPHY.fontSize.sm,
	height: "40px",
	background: COLORS.white,
	color: COLORS.gray700,
	outline: "none",
	transition: "border-color 0.2s",
	cursor: "pointer",
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Table Styles
export const tableStyle = {
	width: "100%",
	borderCollapse: "separate",
	borderSpacing: 0,
	background: COLORS.white,
	fontSize: TYPOGRAPHY.fontSize.sm,
	minWidth: "900px",
	boxShadow: SHADOWS.sm,
	marginBottom: 0,
	fontFamily: TYPOGRAPHY.fontFamily,
	borderRadius: BORDER_RADIUS.lg,
	overflow: "hidden",
}

export const thStyle = {
	padding: `${SPACING.sm} 10px`,
	borderBottom: `2px solid ${COLORS.border}`,
	fontWeight: TYPOGRAPHY.fontWeight.bold,
	background: COLORS.gray50,
	color: COLORS.gray800,
	textAlign: "left",
	fontSize: TYPOGRAPHY.fontSize.sm,
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const tdStyle = {
	padding: `${SPACING.sm} 10px`,
	borderBottom: `1px solid ${COLORS.borderLight}`,
	color: COLORS.gray700,
	fontSize: TYPOGRAPHY.fontSize.sm,
	verticalAlign: "top",
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Tab Styles
export const tabContainerStyle = {
	display: "flex",
	borderBottom: `1.5px solid ${COLORS.border}`,
	marginBottom: 0,
	flexWrap: "wrap",
	background: COLORS.backgroundLight,
	borderRadius: `${BORDER_RADIUS.xl} ${BORDER_RADIUS.xl} 0 0`,
	overflow: "hidden",
	boxShadow: `0 2px 8px ${COLORS.gray100}`,
	position: "relative",
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const tabStyle = {
	flex: 1,
	padding: "14px 0 12px 0",
	border: "none",
	borderBottom: "3px solid transparent",
	background: "none",
	color: COLORS.gray600,
	fontSize: TYPOGRAPHY.fontSize.sm,
	cursor: "pointer",
	outline: "none",
	transition: "color 0.2s, border-bottom 0.2s, background 0.2s",
	borderRight: `1px solid ${COLORS.border}`,
	borderRadius: 0,
	position: "relative",
	zIndex: 1,
	letterSpacing: "0.2px",
	textTransform: "uppercase",
	backgroundClip: "padding-box",
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const activeTabStyle = {
	...tabStyle,
	color: COLORS.primary,
	fontWeight: TYPOGRAPHY.fontWeight.bold,
	borderBottom: `3px solid ${COLORS.primary}`,
	boxShadow: `0 2px 8px ${COLORS.primaryLight}`,
	zIndex: 2,
}

// Button Styles
export const primaryButtonStyle = {
	background: COLORS.primary,
	color: COLORS.white,
	border: "none",
	borderRadius: BORDER_RADIUS.md,
	padding: `${SPACING.xs} ${SPACING.sm}`,
	fontSize: TYPOGRAPHY.fontSize.sm,
	fontWeight: TYPOGRAPHY.fontWeight.medium,
	cursor: "pointer",
	outline: "none",
	fontFamily: TYPOGRAPHY.fontFamily,
	transition: "all 0.2s ease",
	"&:hover": {
		background: COLORS.primaryDark,
		transform: "translateY(-1px)",
		boxShadow: SHADOWS.md,
	},
}

export const secondaryButtonStyle = {
	...primaryButtonStyle,
	background: COLORS.white,
	color: COLORS.primary,
	border: `1px solid ${COLORS.primary}`,
	"&:hover": {
		background: COLORS.primaryLight,
		color: COLORS.primaryDark,
	},
}

export const dangerButtonStyle = {
	...primaryButtonStyle,
	background: COLORS.danger,
	"&:hover": {
		background: COLORS.dangerDark,
	},
}

export const successButtonStyle = {
	...primaryButtonStyle,
	background: COLORS.success,
	"&:hover": {
		background: COLORS.successDark,
	},
}

export const warningButtonStyle = {
	...primaryButtonStyle,
	background: COLORS.warning,
	"&:hover": {
		background: COLORS.warningDark,
	},
}

// Status Badge
export const statusBadgeStyle = (isActive) => ({
	display: "inline-flex",
	alignItems: "center",
	background: isActive ? COLORS.successLight : COLORS.dangerLight,
	padding: `${SPACING.xs} 10px`,
	borderRadius: BORDER_RADIUS.full,
	fontWeight: TYPOGRAPHY.fontWeight.semibold,
	color: isActive ? COLORS.successDark : COLORS.dangerDark,
	fontSize: TYPOGRAPHY.fontSize.sm,
	border: `1px solid ${isActive ? COLORS.success : COLORS.danger}`,
})

// State Styles
export const emptyBoxStyle = {
	color: COLORS.gray600,
	background: COLORS.gray100,
	borderRadius: BORDER_RADIUS.sm,
	padding: `${SPACING.lg} ${SPACING.md}`,
	minHeight: "60px",
	textAlign: "center",
	fontFamily: TYPOGRAPHY.fontFamily,
	fontSize: TYPOGRAPHY.fontSize.sm,
	fontStyle: "italic",
}

export const loadingStyle = {
	padding: SPACING.md,
	color: COLORS.gray500,
	fontFamily: TYPOGRAPHY.fontFamily,
	textAlign: "center",
	fontSize: TYPOGRAPHY.fontSize.sm,
}

export const backButtonStyle = {
	background: "none",
	border: "none",
	borderRadius: BORDER_RADIUS.md,
	padding: `${SPACING.xs} ${SPACING.sm}`,
	marginBottom: SPACING.sm,
	cursor: "pointer",
	fontFamily: TYPOGRAPHY.fontFamily,
	fontSize: TYPOGRAPHY.fontSize.md,
	color: COLORS.primary,
	fontWeight: TYPOGRAPHY.fontWeight.medium,
	outline: "none",
	transition: "all 0.2s ease",
	"&:hover": {
		background: COLORS.primaryLight,
		transform: "translateX(-2px)",
	},
}

// Form Styles
export const inputStyle = {
	padding: `10px 14px`,
	border: `1px solid ${COLORS.gray300}`,
	borderRadius: BORDER_RADIUS.md,
	fontSize: TYPOGRAPHY.fontSize.sm,
	fontFamily: TYPOGRAPHY.fontFamily,
	outline: "none",
	transition: "border-color 0.2s ease",
	"&:focus": {
		borderColor: COLORS.primary,
		boxShadow: `0 0 0 3px ${COLORS.primaryLight}`,
	},
}

export const selectStyle = {
	...inputStyle,
	cursor: "pointer",
}

// Pagination Styles
export const paginationButtonStyle = (disabled, isActive = false) => ({
	padding: `${SPACING.xs} ${SPACING.md}`,
	borderRadius: BORDER_RADIUS.sm,
	border: `1px solid ${COLORS.border}`,
	background: isActive ? COLORS.primary : COLORS.white,
	color: disabled ? COLORS.gray400 : isActive ? COLORS.white : COLORS.gray700,
	cursor: disabled ? "not-allowed" : "pointer",
	fontWeight: isActive ? TYPOGRAPHY.fontWeight.semibold : TYPOGRAPHY.fontWeight.normal,
	minWidth: "36px",
	fontFamily: TYPOGRAPHY.fontFamily,
	fontSize: TYPOGRAPHY.fontSize.sm,
	outline: "none",
	transition: "all 0.2s ease",
	opacity: disabled ? 0.5 : 1,
})

// Modal Styles
export const modalOverlayStyle = {
	position: "fixed",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	zIndex: 1000,
	backdropFilter: "blur(4px)",
}

export const modalContentStyle = {
	backgroundColor: COLORS.white,
	padding: SPACING.lg,
	borderRadius: BORDER_RADIUS.lg,
	minWidth: "400px",
	maxWidth: "600px",
	maxHeight: "80vh",
	overflow: "auto",
	boxShadow: SHADOWS.xl,
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Responsive CSS
export const responsiveCSS = `
  @media (max-width: ${BREAKPOINTS.tablet}) {
    .grid-row {
      grid-template-columns: 1fr !important;
      gap: 18px 0 !important;
    }
    .card-style {
      padding: 20px 8px !important;
    }
    .section-title {
      font-size: 18px !important;
      padding-bottom: 6px !important;
    }
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    .card-style {
      padding: 12px 2vw !important;
    }
    .section-title {
      font-size: 16px !important;
    }
    .tab-container {
      flex-direction: column !important;
    }
    .tab {
      border-right: none !important;
      border-bottom: 1px solid ${COLORS.border} !important;
    }
  }
`

// JSON Highlight CSS
export const jsonHighlightCSS = `
  pre .key {
    color: ${COLORS.primary};
    font-weight: ${TYPOGRAPHY.fontWeight.medium};
  }
  pre .string {
    color: ${COLORS.success};
  }
  pre .number {
    color: ${COLORS.danger};
  }
  pre .boolean {
    color: ${COLORS.warning};
  }
  pre .null {
    color: ${COLORS.gray400};
  }
`

// Main Container Styles
export const mainContainerStyle = {
	// maxWidth: 1200,
	paddingLeft: SPACING.lg,
	paddingRight: SPACING.lg,
	margin: "0 auto",
	fontFamily: TYPOGRAPHY.fontFamily,
	color: COLORS.gray900,
}

export const headerSectionStyle = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	marginBottom: SPACING.lg,
}

export const headerTitleStyle = {
	margin: 0,
	fontSize: TYPOGRAPHY.fontSize.xl,
	fontWeight: TYPOGRAPHY.fontWeight.bold,
	color: COLORS.gray900,
	fontFamily: TYPOGRAPHY.fontFamily,
}

// Pagination Container Style
export const paginationContainerStyle = {
	marginTop: SPACING.xl,
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	gap: SPACING.md,
	fontFamily: TYPOGRAPHY.fontFamily,
}

export const paginationInfoStyle = {
	fontSize: TYPOGRAPHY.fontSize.sm,
	color: COLORS.gray600,
	fontFamily: TYPOGRAPHY.fontFamily,
}
